import { useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Trophy } from "lucide-react";
import { Die } from "@/components/Dice";
import { SoundToggle } from "@/components/SoundToggle";
import { ShareResult } from "@/components/ShareResult";
import { useI18n } from "@/i18n/I18nContext";
import { playDice, playWin } from "@/lib/sound";

const d6 = () => (crypto.getRandomValues(new Uint32Array(1))[0] % 6) + 1;
const fresh = (n) => ({ seats: Array.from({ length: n }, (_, i) => i), queue: Array.from({ length: n }, (_, i) => i), rolls: {}, round: 1, winner: null, tie: [] });

const Seat = ({ i, roll, active, winner, t }) => (
  <div className={`flex items-center gap-3 rounded-2xl border p-3 transition-colors ${active ? "border-amber-500/60 bg-amber-500/10" : winner ? "border-emerald-500/50 bg-emerald-500/10" : "border-white/10 bg-white/[0.03]"}`} data-testid={`dice-practice-seat-${i}`}>
    <span className="min-w-0 flex-1 truncate font-semibold">{t("demo.seat", { n: i + 1 })}</span>
    {roll ? <div className="flex items-center gap-2">{roll.map((v, k) => <Die key={k} value={v} size={34} flat ivory={k === 1} />)}<span className="w-8 text-right font-display text-lg font-bold">{roll[0] + roll[1]}</span></div> : <span className="text-xs text-slate-500">—</span>}
    {winner && <Trophy className="h-5 w-5 text-emerald-400" />}
  </div>
);

export default function DicePracticePage() {
  const { t } = useI18n();
  const [n, setN] = useState(4);
  const [g, setG] = useState(() => fresh(4));
  const [big, setBig] = useState({ a: 6, b: 5, spins: 0 });
  const pick = (k) => { setN(k); setG(fresh(k)); };
  const roll = () => {
    const seat = g.queue[0];
    const r = [d6(), d6()];
    setBig((b) => ({ a: r[0], b: r[1], spins: b.spins + 2 }));
    playDice();
    const rolls = { ...g.rolls, [seat]: r };
    const queue = g.queue.slice(1);
    if (queue.length) { setG({ ...g, rolls, queue }); return; }
    const inRound = g.tie.length ? g.tie : g.seats;
    const best = Math.max(...inRound.map((s) => rolls[s][0] + rolls[s][1]));
    const top = inRound.filter((s) => rolls[s][0] + rolls[s][1] === best);
    if (top.length === 1) { setTimeout(playWin, 600); setG({ ...g, rolls, queue, winner: top[0] }); return; }
    setG({ ...g, rolls, queue: top, tie: top, round: g.round + 1 });
  };
  const cur = g.queue[0];
  return (
    <div className="mx-auto max-w-xl space-y-5 rise" data-testid="dice-practice-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-display text-2xl font-extrabold">{t("demo.practiceTitle")}</h1>
          <p className="text-sm text-slate-400">{t("demo.practiceSub")}</p>
        </div>
        <SoundToggle />
      </div>
      <div className="flex flex-wrap gap-2" data-testid="dice-practice-options">
        {[2, 3, 4, 5, 6].map((k) => (
          <button key={k} type="button" onClick={() => pick(k)} data-testid={`dice-practice-players-${k}`}
            className={`h-9 rounded-full px-4 text-xs font-semibold transition-colors ${n === k ? "bg-amber-500 text-[#0B0D12]" : "border border-white/10 text-slate-300"}`}>{k} {t("ludo.players")}</button>
        ))}
      </div>
      <div className="surface flex justify-center gap-6 p-8" data-testid="dice-practice-dice"><Die value={big.a} size={80} spins={big.spins} /><Die value={big.b} size={66} spins={big.spins} ivory /></div>
      {g.tie.length > 0 && g.winner === null && <p className="text-center text-sm font-semibold text-amber-300" data-testid="dice-practice-tie">{t("demo.tieAgain", { p: g.tie.map((s) => t("demo.seat", { n: s + 1 })).join(", ") })}</p>}
      <div className="space-y-2">{g.seats.map((s) => <Seat key={s} i={s} roll={g.rolls[s]} active={s === cur && g.winner === null} winner={s === g.winner} t={t} />)}</div>
      {g.winner === null ? (
        <button type="button" onClick={roll} className="pulse-ring h-14 w-full rounded-full bg-amber-500 font-display text-lg font-bold text-[#0B0D12] active:scale-95" data-testid="dice-practice-roll">{t("demo.rollFor", { p: t("demo.seat", { n: cur + 1 }) })}</button>
      ) : (
        <div className="surface flex flex-wrap items-center gap-3 p-5" data-testid="dice-practice-end">
          <Trophy className="h-6 w-6 text-amber-400" />
          <p className="flex-1 font-display text-xl font-bold" data-testid="dice-practice-winner">{t("demo.practiceWin", { p: t("demo.seat", { n: g.winner + 1 }) })}</p>
          <ShareResult game={t("share.dice")} result={`${t("demo.practiceWin", { p: t("demo.seat", { n: g.winner + 1 }) })} (${g.rolls[g.winner][0] + g.rolls[g.winner][1]})`} path="/des/essai" />
          <button type="button" onClick={() => setG(fresh(n))} className="flex h-11 items-center gap-2 rounded-full bg-amber-500 px-5 text-sm font-bold text-[#0B0D12]" data-testid="dice-practice-again"><RotateCcw className="h-4 w-4" />{t("ludo.newGame")}</button>
        </div>
      )}
      <Link to="/lobby" className="block text-center text-sm text-amber-300 underline" data-testid="dice-practice-back">{t("dames.backLobby")}</Link>
    </div>
  );
}
